/**
 * Home page — main dashboard after login.
 * Shows the user's team, game selection, ready-up, and matchmaking.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useNostrUser } from "@/hooks/useNostr";
import { useTeam } from "@/hooks/useCommunity";
import { useMatchmaking } from "@/hooks/useMatchmaking";
import { TeamView } from "@/components/TeamView";
import { GameLobby } from "@/components/GameLobby";
import { MatchmakingOverlay } from "@/components/MatchmakingOverlay";
import { Swords, Plus, Loader2 } from "lucide-react";
import { DEFAULT_RATING } from "@/lib/ratings";

export function HomePage() {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useNostrUser();
  const { team, isLoading, hasTeam, createTeam } = useTeam();

  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [searchTime, setSearchTime] = useState(0);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");

  const memberPubkeys = useMemo(() => {
    if (!team) return [];
    return team.memberPubkeys;
  }, [team]);

  const matchmaking = useMatchmaking(
    team?.id ?? null,
    team?.name ?? "",
    memberPubkeys,
  );

  const handleToggleReady = (gameType: string) => {
    if (selectedGame !== gameType) {
      setSelectedGame(gameType);
    }
    matchmaking.toggleReady(gameType);
  };

  const handleStartMatchmaking = () => {
    if (!selectedGame) return;
    setSearchTime(0);
    matchmaking.startMatchmaking(selectedGame, DEFAULT_RATING);

    const startTime = Date.now();
    const interval = setInterval(() => {
      setSearchTime(Date.now() - startTime);
    }, 100);
    // Cleanup handled by overlay unmount
  };

  const handleCancelMatchmaking = () => {
    matchmaking.cancelMatchmaking();
    setSearchTime(0);
  };

  const handleCreateTeam = () => {
    if (newTeamName.trim()) {
      createTeam(newTeamName.trim());
      setShowCreateTeam(false);
      setNewTeamName("");
    }
  };

  // When matched, navigate to game page
  if (matchmaking.phase === "matched" && matchmaking.match) {
    setTimeout(() => {
      navigate(`/game/${matchmaking.match!.gameId}?teamId=${team?.id}`);
    }, 500);
  }

  if (!isLoggedIn) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
          <p className="mt-3 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">Your team. Your games. Your glory.</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !hasTeam ? (
        /* No team yet — prompt to create one */
        <div className="rounded-2xl border border-dashed border-border p-12 text-center">
          <Swords className="mx-auto mb-4 h-12 w-12 text-muted-foreground/40" />
          <h2 className="mb-2 text-xl font-bold text-foreground">Create Your Team</h2>
          <p className="mb-6 text-muted-foreground">
            Give your team a name and start competing.
          </p>
          {showCreateTeam ? (
            <div className="mx-auto flex max-w-sm gap-2">
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateTeam()}
                placeholder="Team name..."
                className="flex-1 rounded-lg border border-border bg-input px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
              <button
                onClick={handleCreateTeam}
                disabled={!newTeamName.trim()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCreateTeam(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              Create Team
            </button>
          )}
        </div>
      ) : team ? (
        /* Has a team */
        <div className="space-y-8">
          {/* Team View */}
          <TeamView
            team={team}
            memberCount={team.memberCount}
            isOwner={team.isOwner}
            readyMembers={matchmaking.readyMembers}
            isReady={matchmaking.isReady}
            onToggleReady={handleToggleReady}
            selectedGame={selectedGame ?? ""}
          />

          {/* Game Lobby */}
          <GameLobby
            selectedGame={selectedGame}
            onSelectGame={setSelectedGame}
          />

          {/* Find Match button */}
          {selectedGame && matchmaking.readyMembers.size >= 2 && matchmaking.phase !== "searching" && (
            <div className="flex justify-center pt-4">
              <button
                onClick={handleStartMatchmaking}
                className="inline-flex items-center gap-3 rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:opacity-90 hover:shadow-xl hover:shadow-primary/30 active:scale-95"
              >
                <Swords className="h-5 w-5" />
                Find Match
              </button>
            </div>
          )}

          {/* Ready hint */}
          {selectedGame && matchmaking.readyMembers.size < 2 && (
            <div className="flex justify-center pt-4">
              <p className="text-sm text-muted-foreground">
                Need at least 2 ready members to find a match. Currently {matchmaking.readyMembers.size} ready.
              </p>
            </div>
          )}

          {/* Ready hint for no game selected */}
          {!selectedGame && (
            <div className="flex justify-center pt-4">
              <p className="text-sm text-muted-foreground">
                Select a game above to start readying up.
              </p>
            </div>
          )}
        </div>
      ) : null}

      {/* Matchmaking overlay */}
      <MatchmakingOverlay
        isOpen={matchmaking.phase === "searching"}
        gameType={selectedGame ?? ""}
        searchTime={searchTime}
        onCancel={handleCancelMatchmaking}
      />
    </div>
  );
}